import React, { useState } from 'react';
import {
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Chip,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StyledCard from '../../components/StyledCard';
import EnhancedTable from '../../components/EnhancedTable';
import { ajaxRejectPurchaseOrder, ajaxApprovePurchaseOrder, ajaxReceivePurchaseOrder } from '../../services/purchaseOrderService';
import { toDateText } from '../../helpers';
import { useNavigate } from 'react-router-dom';
import inMemoryUser from '../../services/inMemoryUser';

export default function RecentPOList({ purchaseOrders }) {
  const navigate = useNavigate();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    orderId: null,
    orderNumber: null
  });

  let columns = [
    {
      id: 'po_date',
      label: 'PO Date',
      sortable: true,
      minWidth: 100,
      format: (value) => toDateText(value),
    },
    {
      id: 'po_number',
      label: 'PO Number',
      minWidth: 120,
      sortable: true,
    },
    {
      id: 'purpose',
      label: 'Purpose',
      minWidth: 200,
      sortable: true,
    },
    {
      id: 'supplier_name',
      label: 'Supplier',
      minWidth: 150,
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      sortable: true,
      format: (value) =>
        <div style={{ textAlign: 'center' }}>
          <Chip
            sx={{color: value === 'draft' ? '#000' : '#fff', height: '18px', fontSize: '12px'}}
            label={
              value === 'draft' ? 'Draft' :
              value === 'waitingapproval' ? 'Waiting Approval' :
              value === 'approved' ? 'Approved' :
              value === 'rejected' ? 'Rejected' :
              value === 'received' ? 'Received' :
              value === 'cancelled' ? 'Cancelled' :
              'Unknown'
            }
            color={
              value === 'draft' ? 'default' : 
              value === 'waitingapproval' ? 'warning' :
              value === 'approved' ? 'success' : 
              value === 'rejected' ? 'error' : 
              value === 'received' ? 'info' : 
              value === 'cancelled' ? 'error' :
              'default'
            }
            size="small"
          />
        </div>
    }
  ];

  if ( inMemoryUser.isAdmin() ) {
    columns.push({
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      sortable: false,
      format: (value, row) => {
        if (row.status === 'draft') {
          return (
            <>
              <Tooltip title="Reject Purchase Order">
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleConfirm('reject', row.id, row.po_number)}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Approve Purchase Order">
                <IconButton
                  color="success"
                  size="small"
                  onClick={() => handleConfirm('approve', row.id, row.po_number)}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            </>
          );
        } else if (row.status === 'approved') {
          return (
            <Tooltip title="Mark as Received">
              <IconButton
                color="info"
                size="small"
                onClick={() => handleConfirm('receive', row.id, row.po_number)}
              >
                <LocalShippingIcon />
              </IconButton>
            </Tooltip>
          );
        }
        return null;
      }
    });
  }

  const handleConfirm = (type, orderId, orderNumber) => {
    setConfirmDialog({
      open: true,
      type,
      orderId,
      orderNumber
    });
  };

  const handleClose = () => {
    setConfirmDialog({
      open: false,
      type: null,
      orderId: null,
      orderNumber: null
    });
  };

  const handleAction = () => {
    const { type, orderId } = confirmDialog;
    let action;
    
    switch(type) {
      case 'approve':
        action = ajaxApprovePurchaseOrder;
        break;
      case 'reject':
        action = ajaxRejectPurchaseOrder;
        break;
      case 'receive':
        action = ajaxReceivePurchaseOrder;
        break;
      default:
        return;
    }
    
    action(orderId)
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.error(`Error ${type}ing PO:`, error);
      })
      .finally(() => {
        handleClose();
      });
  };

  const getDialogTitle = () => {
    switch(confirmDialog.type) {
      case 'approve':
        return 'Approve Purchase Order';
      case 'reject':
        return 'Reject Purchase Order';
      case 'receive':
        return 'Mark Purchase Order as Received';
      default:
        return '';
    }
  };

  const getActionButtonColor = () => {
    switch(confirmDialog.type) {
      case 'approve':
        return 'success';
      case 'reject':
        return 'error';
      case 'receive':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getActionButtonText = () => {
    switch(confirmDialog.type) {
      case 'approve':
        return 'Approve';
      case 'reject':
        return 'Reject';
      case 'receive':
        return 'Mark as Received';
      default:
        return '';
    }
  };

  return (
    <>
      <StyledCard
        title="Recent Purchase Orders (Last 30 Days)"
        titleIcon={HistoryIcon}
      >
        <TableContainer component={Paper}>
          <EnhancedTable
            columns={columns}
            items={purchaseOrders}
            orderBy="po_date"
            order="desc"
            noPagination
            border
            onRowClick={(id) => navigate('/purchaseorders/edit/' + id + '?page=dashboard')}
            noItemsMessage="No purchase orders found"
          />
        </TableContainer>
      </StyledCard>

      <Dialog
        open={confirmDialog.open}
        onClose={handleClose}
      >
        <DialogTitle>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to {confirmDialog.type} Purchase Order #{confirmDialog.orderNumber}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleAction}
            color={getActionButtonColor()}
            variant="contained"
          >
            {getActionButtonText()}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
} 